import { useState, useEffect, useCallback, useMemo } from "react";
import "../obihub-os/pages/products.css";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { cn, formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import EmptyState from "@/components/common/EmptyState";
import { TableRowSkeleton } from "@/components/common/Skeletons";
import {
  Search,
  Package,
  BarChart,
  AlertTriangle,
  RefreshCw,
  Loader2,
  ImageIcon,
  Eye,
  Settings2,
  PlusCircle,
  Link2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  FileCode2,
  MoreHorizontal,
} from "lucide-react";

const providerLogoMap = {
  parasut: "/integrations/parasut.svg",
  bizimhesap: "/integrations/bizimhesap.svg",
  woocommerce: "/integrations/woocommerce.svg",
  trendyol: "/integrations/trendyol.svg",
  hepsiburada: "/integrations/hepsiburada.svg",
  amazon: "/integrations/amazon.svg",
  n11: "/integrations/n11.png",
  idefix: "/integrations/idefix.svg",
  pazarama: "/integrations/pazarama.svg",
  shopify: "/integrations/shopify.svg",
  ikas: "/integrations/ikas.svg",
  shopier: "/integrations/shopier.svg",
  benimpos: "/integrations/benimpos.png?v=20261030",
};

const matchableProviders = new Set([
  "woocommerce",
  "trendyol",
  "hepsiburada",
  "amazon",
  "n11",
  "idefix",
  "pazarama",
  "shopify",
  "ikas",
  "shopier",
  "benimpos",
]);

const accountingProviderLabel = {
  parasut: "Paraşüt",
  bizimhesap: "BizimHesap",
  benimpos: "BenimPOS",
};

const IntegrationLogo = ({ provider, matched, onClick, title }) => {
  const src = providerLogoMap[provider];
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-7 w-7 rounded-md border bg-card p-1 transition",
        matched ? "border-emerald-300 hover:border-emerald-400" : "border-border hover:border-border"
      )}
      title={title}
    >
      {src ? (
        <img
          src={src}
          alt={provider}
          className={cn("h-full w-full object-contain", !matched && "grayscale opacity-50")}
        />
      ) : (
        <span className={cn("text-[10px] font-semibold", !matched && "text-muted-foreground")}>{provider.slice(0, 2).toUpperCase()}</span>
      )}
    </button>
  );
};

export default function ProductsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [linkFilter, setLinkFilter] = useState("all");
  const [imageFilter, setImageFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchProduct, setMatchProduct] = useState(null);
  const [matchIntegration, setMatchIntegration] = useState(null);
  const [matchSearch, setMatchSearch] = useState("");
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchCandidates, setMatchCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [savingMatch, setSavingMatch] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [addOpen, setAddOpen] = useState(false);
  const [accountingProvider, setAccountingProvider] = useState("");
  const [importingAccounting, setImportingAccounting] = useState(false);
  const [importingInStockOnly, setImportingInStockOnly] = useState(false);
  const [bulkMatchingBizimhesap, setBulkMatchingBizimhesap] = useState(false);
  const [bulkMatchingSelectedBizimhesap, setBulkMatchingSelectedBizimhesap] = useState(false);
  const [bulkMatchingBenimpos, setBulkMatchingBenimpos] = useState(false);
  const [bulkMatchingSelectedBenimpos, setBulkMatchingSelectedBenimpos] = useState(false);
  const [pushingSelectedToWoo, setPushingSelectedToWoo] = useState(false);
  const [pushingAllToWoo, setPushingAllToWoo] = useState(false);
  const [deletingSelectedFromWoo, setDeletingSelectedFromWoo] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  const fetchData = useCallback(async () => {
    try {
      const [prods, integs, accounting, cats] = await Promise.all([
        api.get("/products"),
        api.get("/integrations"),
        api.get("/settings/accounting").catch(() => ({})),
        api.get("/categories").catch(() => []),
      ]);
      setProducts(prods);
      setIntegrations(integs);
      setCategories(cats);
      setAccountingProvider(String(accounting?.provider || "").toLowerCase());
      setSelectedIds(new Set());
    } catch (error) {
      console.error("Failed to fetch:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredProducts = products.filter((product) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      (product.name || "").toLowerCase().includes(q) ||
      (product.sku || "").toLowerCase().includes(q) ||
      (product.barcode || "").includes(searchQuery);
    const categoryParts = String(product.category || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    const matchesCategory =
      categoryFilter === "all" ||
      (categoryFilter.startsWith("id:") && (
        product.category_id === categoryFilter.slice(3) ||
        product.subcategory_id === categoryFilter.slice(3)
      )) ||
      categoryParts.includes(categoryFilter);
    const stock = product.stock || 0;
    const matchesStock = stockFilter === "all" ||
      (stockFilter === "low" && stock > 0 && stock < 10) ||
      (stockFilter === "out" && stock === 0) ||
      (stockFilter === "available" && stock >= 10);
    const hasWoo = !!(product?.channel_links?.woocommerce || product?.wc_id);
    const hasBizimHesap = !!(product?.channel_links?.bizimhesap);
    const hasImage = !!(
      String(product?.image_url || "").trim() ||
      (Array.isArray(product?.images) &&
        product.images.some((img) => {
          if (typeof img === "string") return String(img || "").trim().length > 0;
          if (img && typeof img === "object") {
            return Boolean(String(img.src || img.url || img.image_url || "").trim());
          }
          return false;
        }))
    );
    const matchesLink =
      linkFilter === "all" ||
      (linkFilter === "matched" && hasBizimHesap && hasWoo) ||
      (linkFilter === "bizim_only" && hasBizimHesap && !hasWoo) ||
      (linkFilter === "woo_only" && hasWoo && !hasBizimHesap);
    const matchesImage =
      imageFilter === "all" ||
      (imageFilter === "with" && hasImage) ||
      (imageFilter === "without" && !hasImage);
    return matchesSearch && matchesCategory && matchesStock && matchesLink && matchesImage;
  });

  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];
    list.sort((a, b) => {
      if (sortField === "stock") {
        const av = Number(a?.stock || 0);
        const bv = Number(b?.stock || 0);
        return sortDirection === "asc" ? av - bv : bv - av;
      }
      if (sortField === "price") {
        const av = Number(a?.price || 0);
        const bv = Number(b?.price || 0);
        return sortDirection === "asc" ? av - bv : bv - av;
      }
      const an = String(a?.name || "").toLocaleLowerCase("tr");
      const bn = String(b?.name || "").toLocaleLowerCase("tr");
      return sortDirection === "asc" ? an.localeCompare(bn, "tr") : bn.localeCompare(an, "tr");
    });
    return list;
  }, [filteredProducts, sortField, sortDirection]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  const sortLabel = (field) => {
    if (sortField !== field) return "";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    const ids = pagedProducts.map((p) => p.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`${ids.length} ürünü kalıcı olarak silmek istediğinize emin misiniz?`)) return;

    setDeleting(true);
    try {
      const result = await api.post("/products/bulk-delete", { product_ids: ids });
      toast.success(`${result?.deleted_count || ids.length} ürün silindi`);
      await fetchData();
    } catch (error) {
      toast.error(error?.message || "Toplu silme başarısız oldu");
    } finally {
      setDeleting(false);
    }
  };

  const wcIntegration = integrations.find(i => i.provider === "woocommerce");

  const handleCreateBlankProduct = async () => {
    try {
      const ts = Date.now().toString().slice(-6);
      const created = await api.post("/products", {
        name: "Yeni Ürün",
        sku: `SKU-${ts}`,
        barcode: "",
        description: "",
        price: 0,
        stock: 0,
        category: "",
        image_url: null,
      });
      toast.success("Boş ürün oluşturuldu");
      setAddOpen(false);
      await fetchData();
      if (created?.id) navigate(`/panel/urunler/${created.id}`);
    } catch (error) {
      toast.error(error?.message || "Boş ürün oluşturulamadı");
    }
  };

  const handleImportFromAccounting = async () => {
    setImportingAccounting(true);
    try {
      const res = await api.post("/products/import-accounting", { limit: 5000, stock_only: true, min_stock: 1 });
      toast.success(
        `${res?.provider || "Muhasebe"}: ${res?.created_count || 0} yeni, ${res?.updated_count || 0} güncellendi`
      );
      setAddOpen(false);
      await fetchData();
    } catch (error) {
      toast.error(error?.message || "Muhasebe ürünleri alınamadı");
    } finally {
      setImportingAccounting(false);
    }
  };

  const handleImportInStockFromBizimhesap = async () => {
    if (accountingProvider !== "bizimhesap") {
      toast.error("Bu işlem sadece BizimHesap aktifken kullanılabilir.");
      return;
    }
    setImportingInStockOnly(true);
    try {
      const res = await api.post("/products/import-accounting", {
        limit: 5000,
        stock_only: true,
        min_stock: 1,
      });
      toast.success(
        `Stoktakiler: ${res?.created_count || 0} yeni, ${res?.updated_count || 0} güncellendi`
      );
      await fetchData();
    } catch (error) {
      toast.error(error?.message || "Stokta olan ürünler alınamadı");
    } finally {
      setImportingInStockOnly(false);
    }
  };

  const handleBulkMatchBizimhesap = async () => {
    setBulkMatchingBizimhesap(true);
    try {
      const res = await api.post("/products/bulk-match-bizimhesap", {
        limit: 10000,
        update_stock_price: true,
      });
      toast.success(`${res?.matched_count || 0} ürün otomatik eşleştirildi`);
      await fetchData();
    } catch (error) {
      toast.error(error?.message || "Toplu eşleştirme yapılamadı");
    } finally {
      setBulkMatchingBizimhesap(false);
    }
  };

  const handleBulkMatchSelectedBizimhesap = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error("Önce ürün seçin.");
      return;
    }
    setBulkMatchingSelectedBizimhesap(true);
    try {
      const res = await api.post("/products/bulk-match-bizimhesap", {
        limit: 10000,
        update_stock_price: true,
        update_identity_fields: true,
        product_ids: ids,
      });
      toast.success(`${res?.matched_count || 0} seçili ürün eşleştirildi`);
      await fetchData();
    } catch (error) {
      toast.error(error?.message || "Seçili ürünlerde toplu eşleştirme yapılamadı");
    } finally {
      setBulkMatchingSelectedBizimhesap(false);
    }
  };

  const handleBulkMatchBenimpos = async () => {
    setBulkMatchingBenimpos(true);
    try {
      const res = await api.post("/products/bulk-match-benimpos", { limit: 10000, update_stock_price: true });
      toast.success(`${res?.matched_count || 0} ürün BenimPOS ile eşleştirildi`);
      await fetchData();
    } catch (error) {
      toast.error(error?.message || "Toplu eşleştirme yapılamadı");
    } finally {
      setBulkMatchingBenimpos(false);
    }
  };

  const handleBulkMatchSelectedBenimpos = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error("Önce ürün seçin."); return; }
    setBulkMatchingSelectedBenimpos(true);
    try {
      const res = await api.post("/products/bulk-match-benimpos", {
        limit: 10000,
        update_stock_price: true,
        update_identity_fields: true,
        product_ids: ids,
      });
      toast.success(`${res?.matched_count || 0} seçili ürün eşleştirildi`);
      await fetchData();
    } catch (error) {
      toast.error(error?.message || "Seçili ürünlerde toplu eşleştirme yapılamadı");
    } finally {
      setBulkMatchingSelectedBenimpos(false);
    }
  };

  const handlePushSelectedToWoo = async () => {
    if (!wcIntegration) {
      toast.error("WooCommerce entegrasyonu bulunamadı. Önce entegrasyon ekleyin.");
      return;
    }
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error("Önce listeden ürün seçin.");
      return;
    }
    setPushingSelectedToWoo(true);
    try {
      const res = await api.post("/products/push-woocommerce", {
        all: false,
        product_ids: ids,
        woo_status: "publish",
      });
      const baseMsg =
        `${res?.requested_count || 0} aday | ` +
        `${res?.created_count || 0} yeni | ${res?.linked_count || 0} eslesme | ` +
        `${res?.failed_count || 0} hata | ` +
        `${res?.image_proxied_count || 0}/${res?.image_source_count || 0} gorsel R2`;
      if ((res?.failed_count || 0) > 0 && Array.isArray(res?.errors) && res.errors.length) {
        toast.error(`${baseMsg} | Ilk hata: ${res.errors[0]}`);
      } else {
        toast.success(baseMsg);
      }
      await fetchData();
    } catch (error) {
      toast.error(error?.message || "Seçili ürünler WooCommerce'e gönderilemedi");
    } finally {
      setPushingSelectedToWoo(false);
    }
  };

  const handlePushAllToWoo = async () => {
    if (!wcIntegration) {
      toast.error("WooCommerce entegrasyonu bulunamadı. Önce entegrasyon ekleyin.");
      return;
    }
    if (!window.confirm("BizimHesap bağlı ve Woo'da olmayan tüm ürünler mağazaya gönderilsin mi?")) return;

    setPushingAllToWoo(true);
    try {
      const res = await api.post("/products/push-woocommerce", {
        all: true,
        woo_status: "publish",
      });
      const baseMsg =
        `${res?.requested_count || 0} aday | ` +
        `${res?.created_count || 0} yeni | ${res?.linked_count || 0} eslesme | ` +
        `${res?.failed_count || 0} hata | ` +
        `${res?.image_proxied_count || 0}/${res?.image_source_count || 0} gorsel R2`;
      if ((res?.failed_count || 0) > 0 && Array.isArray(res?.errors) && res.errors.length) {
        toast.error(`${baseMsg} | Ilk hata: ${res.errors[0]}`);
      } else {
        toast.success(baseMsg);
      }
      await fetchData();
    } catch (error) {
      toast.error(error?.message || "Ürünler WooCommerce'e gönderilemedi");
    } finally {
      setPushingAllToWoo(false);
    }
  };

  const handleDeleteSelectedFromWoo = async () => {
    if (!wcIntegration) {
      toast.error("WooCommerce entegrasyonu bulunamadı. Önce entegrasyon ekleyin.");
      return;
    }
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error("Önce listeden ürün seçin.");
      return;
    }
    if (!window.confirm(`Seçili ${ids.length} ürün WooCommerce'den silinsin mi?`)) return;

    setDeletingSelectedFromWoo(true);
    try {
      const res = await api.post("/products/delete-woocommerce", {
        product_ids: ids,
      });
      toast.success(
        `${res?.deleted_count || 0} ürün Woo'dan silindi` +
        ((res?.unlinked_only_count || 0) > 0 ? `, ${res?.unlinked_only_count || 0} ürünün sadece bağlantısı temizlendi` : "")
      );
      await fetchData();
    } catch (error) {
      toast.error(error?.message || "Seçili ürünler WooCommerce'den silinemedi");
    } finally {
      setDeletingSelectedFromWoo(false);
    }
  };

  const handleSyncProducts = async () => {
    if (!wcIntegration) {
      toast.error("WooCommerce entegrasyonu bulunamadı. Önce entegrasyon ekleyin.");
      return;
    }
    setSyncing(true);
    try {
      const result = await api.post(`/woocommerce/${wcIntegration.id}/sync-products`);
      if (result.success) {
        toast.success(result.message);
        await fetchData();
      }
    } catch (error) {
      toast.error(error.message || "Ürünler senkronize edilemedi");
    } finally {
      setSyncing(false);
    }
  };

  // Legacy string categories (products without category_id)
  const legacyCategories = [
    ...new Set(
      products
        .filter(p => !p.category_id)
        .flatMap((p) =>
          String(p.category || "")
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean)
        )
    ),
  ];
  const stats = {
    total: products.length,
    lowStock: products.filter(p => (p.stock || 0) > 0 && (p.stock || 0) < 10).length,
    outOfStock: products.filter(p => (p.stock || 0) === 0).length,
    totalValue: products.reduce((sum, p) => sum + ((p.price || 0) * (p.stock || 0)), 0),
  };

  const integrationTargets = integrations.filter((i) => matchableProviders.has(String(i.provider || "").toLowerCase()));
  const accountingDetailTarget = accountingProvider
    ? [{ id: `__${accountingProvider}__`, provider: accountingProvider, name: accountingProviderLabel[accountingProvider] || accountingProvider }]
    : [];
  const detailTargets = [...accountingDetailTarget, ...integrationTargets];

  const getLinkValue = (product, provider) => {
    if (!product) return "";
    const p = String(provider || "").toLowerCase();
    if (p === "woocommerce") {
      return product?.channel_links?.woocommerce || product?.wc_id || "";
    }
    if (p === "parasut") {
      return product?.parasut_product_id || product?.channel_links?.parasut || "";
    }
    return product?.channel_links?.[p] || "";
  };

  const fetchMatchCandidates = async (integrationId, queryText = "", currentProductId = "") => {
    if (!integrationId) return;
    setMatchLoading(true);
    try {
      const res = await api.get(
        `/integrations/${integrationId}/source-products?page=1&per_page=25&search=${encodeURIComponent(queryText || "")}`
      );
      const items = Array.isArray(res?.items) ? res.items : [];
      setMatchCandidates(items.filter((x) => x.id !== currentProductId && x.external_id));
    } catch (error) {
      toast.error(error.message || "Mağaza ürünleri alınamadı");
      setMatchCandidates([]);
    } finally {
      setMatchLoading(false);
    }
  };

  const openMatchDialog = async (product, integration) => {
    const initialQuery = product?.name || product?.sku || "";
    setMatchProduct(product);
    setMatchIntegration(integration);
    setMatchSearch(initialQuery);
    setSelectedCandidate(null);
    setMatchCandidates([]);
    setMatchOpen(true);
    await fetchMatchCandidates(integration?.id, initialQuery, product?.id);
  };

  const saveMatch = async () => {
    if (!matchProduct?.id || !matchIntegration?.provider || !selectedCandidate?.external_id) return;
    setSavingMatch(true);
    try {
      await api.post(`/products/${matchProduct.id}/link-channel`, {
        provider: String(matchIntegration.provider || "").toLowerCase(),
        external_id: String(selectedCandidate.external_id),
        source_product_id: selectedCandidate.id,
      });
      toast.success("Ürün eşleştirildi");
      setMatchOpen(false);
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Eşleştirme kaydedilemedi");
    } finally {
      setSavingMatch(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, categoryFilter, stockFilter, linkFilter, imageFilter, pageSize, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pagedProducts = sortedProducts.slice(pageStart, pageStart + pageSize);
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    categoryFilter !== "all" ||
    stockFilter !== "all" ||
    linkFilter !== "all" ||
    imageFilter !== "all";
  const activeFilterCount = [
    searchQuery.trim().length > 0,
    categoryFilter !== "all",
    stockFilter !== "all",
    linkFilter !== "all",
    imageFilter !== "all",
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearchQuery("");
    setCategoryFilter("all");
    setStockFilter("all");
    setLinkFilter("all");
    setImageFilter("all");
    setPage(1);
  };

  return (
    <div className="page-shell space-y-6 overflow-x-hidden" data-testid="products-page" data-os-page="products">
      {/* Header */}
      <div className="page-header os-products-command">
        <div className="app-page-header">
          <h1 className="app-page-title">Ürünler</h1>
          <p className="app-page-subtitle">
            {loading ? "Yükleniyor…" : `${products.length.toLocaleString("tr-TR")} ürün · Entegrasyonlarınızı buradan yönetin`}
          </p>
        </div>
        <div className="page-header-actions flex-col os-products-actions">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="add-product-actions-btn">
              <PlusCircle className="mr-2 h-4 w-4" />
              Ürün Ekle
            </Button>
            <Button
              size="sm"
              onClick={handleSyncProducts}
              disabled={syncing}
              data-testid="sync-products-btn"
            >
              {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              {syncing ? "Senkronize ediliyor..." : "Mağaza Ürünlerini Çek"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/panel/urunler/ayarlar")} data-testid="product-settings-btn">
              <Settings2 className="mr-2 h-4 w-4" />
              Ayarlar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              disabled={deleting || selectedIds.size === 0}
              data-testid="bulk-delete-products-btn"
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              {deleting ? "Siliniyor..." : `Seçilenleri Sil (${selectedIds.size})`}
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 rounded-xl border border-border bg-muted/50/70 p-2 sm:justify-end">
            {accountingProvider === "benimpos" && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <MoreHorizontal className="mr-2 h-4 w-4" />
                      Toplu İşlemler
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuItem onClick={handleBulkMatchBenimpos} disabled={bulkMatchingBenimpos}>
                      {bulkMatchingBenimpos ? "Eşleştiriliyor..." : "Toplu Eşleştir (BenimPOS)"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handlePushAllToWoo} disabled={pushingAllToWoo}>
                      {pushingAllToWoo ? "Gönderiliyor..." : "Tümünü Mağazaya Gönder"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" disabled={selectedIds.size === 0}>
                      <Link2 className="mr-2 h-4 w-4" />
                      Seçili İşlemler ({selectedIds.size})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <DropdownMenuItem onClick={handleBulkMatchSelectedBenimpos} disabled={bulkMatchingSelectedBenimpos || selectedIds.size === 0}>
                      {bulkMatchingSelectedBenimpos ? "Eşleştiriliyor..." : `Seçilenleri Eşleştir (${selectedIds.size})`}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handlePushSelectedToWoo} disabled={pushingSelectedToWoo || selectedIds.size === 0}>
                      {pushingSelectedToWoo ? "Gönderiliyor..." : `Seçilenleri Mağazaya Gönder (${selectedIds.size})`}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            {accountingProvider === "bizimhesap" && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" data-testid="bulk-actions-menu-trigger">
                      <MoreHorizontal className="mr-2 h-4 w-4" />
                      Toplu İşlemler
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuItem
                      onClick={handleImportInStockFromBizimhesap}
                      disabled={importingInStockOnly}
                      data-testid="import-bizimhesap-instock-btn"
                    >
                      {importingInStockOnly ? "Çekiliyor..." : "Stoktakileri Çek"}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleBulkMatchBizimhesap}
                      disabled={bulkMatchingBizimhesap}
                      data-testid="bulk-match-bizimhesap-btn"
                    >
                      {bulkMatchingBizimhesap ? "Eşleştiriliyor..." : "Toplu Eşleştir"}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handlePushAllToWoo}
                      disabled={pushingAllToWoo}
                      data-testid="push-all-woo-btn"
                    >
                      {pushingAllToWoo ? "Gönderiliyor..." : "Tümünü Mağazaya Gönder"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" disabled={selectedIds.size === 0} data-testid="selected-actions-menu-trigger">
                      <Link2 className="mr-2 h-4 w-4" />
                      Seçili İşlemler ({selectedIds.size})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <DropdownMenuItem
                      onClick={handleBulkMatchSelectedBizimhesap}
                      disabled={bulkMatchingSelectedBizimhesap || selectedIds.size === 0}
                      data-testid="bulk-match-selected-bizimhesap-btn"
                    >
                      {bulkMatchingSelectedBizimhesap ? "Eşleştiriliyor..." : `Seçilenleri Eşleştir (${selectedIds.size})`}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handlePushSelectedToWoo}
                      disabled={pushingSelectedToWoo || selectedIds.size === 0}
                      data-testid="push-selected-woo-btn"
                    >
                      {pushingSelectedToWoo ? "Gönderiliyor..." : `Seçilenleri Mağazaya Gönder (${selectedIds.size})`}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleDeleteSelectedFromWoo}
                      disabled={deletingSelectedFromWoo || selectedIds.size === 0}
                      data-testid="delete-selected-woo-btn"
                    >
                      {deletingSelectedFromWoo ? "Siliniyor..." : `Seçilenleri Woo'da Sil (${selectedIds.size})`}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4 os-products-insights">
        <div className="app-stat-card p-5" data-testid="stat-total-products">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.07em] text-muted-foreground">Toplam Ürün</p>
              <p className="app-stat-value mt-1">{stats.total}</p>
            </div>
            <Package className="h-8 w-8 text-muted-foreground/40" />
          </div>
        </div>
        <div className="app-stat-card p-5" data-testid="stat-low-stock">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.07em] text-muted-foreground">Düşük Stok</p>
              <p className="app-stat-value mt-1 text-amber-600">{stats.lowStock}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-amber-400/50" />
          </div>
        </div>
        <div className="app-stat-card p-5" data-testid="stat-out-of-stock">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.07em] text-muted-foreground">Stok Bitti</p>
              <p className="app-stat-value mt-1 text-red-600">{stats.outOfStock}</p>
            </div>
            <Package className="h-8 w-8 text-red-400/50" />
          </div>
        </div>
        <div className="app-stat-card p-5" data-testid="stat-total-value">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.07em] text-muted-foreground">Stok Değeri</p>
              <p className="app-stat-value mt-1">{formatCurrency(stats.totalValue)}</p>
            </div>
            <BarChart className="h-8 w-8 text-muted-foreground/40" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="surface-card p-5 os-products-filters">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="section-label">Filtreler</span>
            {hasActiveFilters && (
              <Badge variant="secondary" className="h-5 px-2 text-[10px]">
                {activeFilterCount} aktif
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{filteredProducts.length} / {products.length} ürün</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              data-testid="clear-products-filters-btn"
            >
              Filtreleri Temizle
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-12">
          <div className="md:col-span-2 xl:col-span-4">
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Arama</label>
            <div className="flex items-center gap-3 rounded-lg border bg-background px-3 h-10">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <input
                className="flex-1 bg-transparent text-sm outline-none"
                placeholder="Ürün adı, stok kodu veya barkod ara"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-products-input"
              />
            </div>
          </div>

          <div className="xl:col-span-2">
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Kategori</label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full" data-testid="category-filter-select"><SelectValue placeholder="Kategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                {categories.filter(c => !c.parent_id).map(parent => (
                  <SelectItem key={`id:${parent.id}`} value={`id:${parent.id}`}>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: parent.color || "#6366f1" }} />
                      {parent.name}
                    </span>
                  </SelectItem>
                ))}
                {categories.filter(c => c.parent_id).length > 0 && (
                  <>
                    <div className="px-2 py-1 text-xs text-muted-foreground font-medium">Alt Kategoriler</div>
                    {categories.filter(c => c.parent_id).map(sub => (
                      <SelectItem key={`id:${sub.id}`} value={`id:${sub.id}`}>
                        <span className="flex items-center gap-1.5 pl-3">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-gray-400" />
                          {sub.name}
                        </span>
                      </SelectItem>
                    ))}
                  </>
                )}
                {legacyCategories.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-xs text-muted-foreground font-medium">Diğer</div>
                    {legacyCategories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="xl:col-span-2">
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Stok</label>
            <Select value={stockFilter} onValueChange={setStockFilter}>
              <SelectTrigger className="w-full" data-testid="stock-filter-select"><SelectValue placeholder="Stok Durumu" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Stoklar</SelectItem>
                <SelectItem value="available">Stokta Var</SelectItem>
                <SelectItem value="low">Düşük Stok</SelectItem>
                <SelectItem value="out">Stok Bitti</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="xl:col-span-2">
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Eşleşme</label>
            <Select value={linkFilter} onValueChange={setLinkFilter}>
              <SelectTrigger className="w-full" data-testid="link-filter-select"><SelectValue placeholder="Entegrasyon Filtresi" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Eşleşmeler</SelectItem>
                <SelectItem value="matched">Eşleşmiş Ürünler</SelectItem>
                <SelectItem value="bizim_only">Sadece BizimHesap'ta</SelectItem>
                <SelectItem value="woo_only">Sadece Woo'da</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="xl:col-span-2">
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Fotoğraf</label>
            <Select value={imageFilter} onValueChange={setImageFilter}>
              <SelectTrigger className="w-full" data-testid="image-filter-select"><SelectValue placeholder="Fotoğraf Durumu" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="with">Fotoğraflı</SelectItem>
                <SelectItem value="without">Fotoğrafsız</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="xl:col-span-2">
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Sayfa Boyutu</label>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="w-full" data-testid="products-page-size-select">
                <SelectValue placeholder="Gösterim" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25 göster</SelectItem>
                <SelectItem value="50">50 göster</SelectItem>
                <SelectItem value="100">100 göster</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="table-shell overflow-hidden os-products-table">
        <div className="sheet-heading">
          <div>
            <h2>Ürünler</h2>
            {!loading && <p>{products.length} ürün</p>}
          </div>
        </div>
          {/* Mobile list */}
          <div className="space-y-2 p-3 md:hidden">
            {loading ? (
              <div className="px-4 py-10 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" /></div>
            ) : pagedProducts.length === 0 ? (
              products.length === 0 ? (
                <EmptyState
                  icon={<Package strokeWidth={1.5} />}
                  title="Henüz ürün yok"
                  desc="Mağaza ürünlerinizi çekerek başlayın veya yeni bir ürün ekleyin."
                  action={{ label: "Ürünleri Çek", onClick: () => document.querySelector('[data-testid="sync-products-btn"]')?.click() }}
                  size="sm"
                />
              ) : (
                <EmptyState
                  icon={<Search strokeWidth={1.5} />}
                  title="Ürün bulunamadı"
                  desc="Arama kriterlerinizi değiştirmeyi deneyin."
                  size="sm"
                />
              )
            ) : pagedProducts.map((product) => {
              const stock = product.stock || 0;
              return (
                <div
                  key={`mobile-${product.id}`}
                  className={cn("rounded-xl border border-border bg-card p-3", stock === 0 && "bg-red-50/30")}
                >
                  <div className="mb-2 flex items-center justify-end">
                    <Checkbox
                      checked={selectedIds.has(product.id)}
                      onCheckedChange={() => toggleSelect(product.id)}
                      aria-label={`Ürün seç ${product.name}`}
                    />
                  </div>
                  <div className="flex items-start gap-3">
                    {product.image_url ? (
                      <img src={product.image_url} alt="" className="h-12 w-12 rounded-lg object-cover border shrink-0" />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center shrink-0"><ImageIcon className="h-4 w-4 text-muted-foreground" /></div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground">{product.name}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Stok: {stock} · Fiyat: {formatCurrency(product.price || 0)}
                      </p>
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        {detailTargets.map((integration) => {
                          const provider = String(integration.provider || "").toLowerCase();
                          const matched = !!getLinkValue(product, provider);
                          if (provider === "parasut" || provider === "bizimhesap" || provider === "benimpos") {
                            return (
                              <IntegrationLogo
                                key={`mobile-${product.id}-${provider}`}
                                provider={provider}
                                matched={matched}
                                title={`${integration.name || provider}${matched ? " · Eşleşti" : " · Eşleşmedi"}`}
                                onClick={() => navigate(`/panel/urunler/${product.id}`)}
                              />
                            );
                          }
                          return (
                            <IntegrationLogo
                              key={`mobile-${product.id}-${integration.id}`}
                              provider={provider}
                              matched={matched}
                              title={`${integration.name || provider}${matched ? " · Eşleşti" : " · Eşleşmedi"}`}
                              onClick={() => openMatchDialog(product, integration)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-full text-xs"
                      onClick={() => navigate(`/panel/urunler/${product.id}`)}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                      Görüntüle
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden md:block w-full overflow-x-auto os-products-scroll">
          <table className="w-full min-w-[980px] table-fixed text-sm">
            <colgroup>
              <col className="w-[52px]" />
              <col />
              <col className="w-[88px]" />
              <col className="w-[140px]" />
              <col className="w-[220px]" />
              <col className="w-[140px]" />
            </colgroup>
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="px-4 py-3 text-left">
                  <Checkbox
                    checked={pagedProducts.length > 0 && pagedProducts.every((p) => selectedIds.has(p.id))}
                    onCheckedChange={toggleAllFiltered}
                    aria-label="Tüm filtrelenmiş ürünleri seç"
                  />
                </th>
                <th className="px-5 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                  <button type="button" className="hover:text-foreground" onClick={() => toggleSort("name")}>Ürün{sortLabel("name")}</button>
                </th>
                <th className="px-4 py-3 text-center text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                  <button type="button" className="hover:text-foreground" onClick={() => toggleSort("stock")}>Stok{sortLabel("stock")}</button>
                </th>
                <th className="px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                  <button type="button" className="hover:text-foreground" onClick={() => toggleSort("price")}>Fiyat{sortLabel("price")}</button>
                </th>
                <th className="px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Entegrasyonlar</th>
                <th className="px-4 py-3 text-right text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={6}><TableRowSkeleton cols={6} rows={8} /></td></tr>
              ) : pagedProducts.length === 0 ? (
                <tr><td colSpan={6}>
                  {products.length === 0 ? (
                    <EmptyState
                      icon={<Package strokeWidth={1.5} />}
                      title="Henüz ürün yok"
                      desc={'"Mağaza Ürünlerini Çek" butonuyla mağazanızdaki ürünleri buraya aktarın.'}
                      action={{ label: "Ürünleri Çek", onClick: () => document.querySelector('[data-testid="sync-products-btn"]')?.click() }}
                    />
                  ) : (
                    <EmptyState icon={<Search strokeWidth={1.5} />} title="Ürün bulunamadı" desc="Farklı bir arama terimi veya filtre deneyin." />
                  )}
                </td></tr>
              ) : pagedProducts.map((product) => {
                const stock = product.stock || 0;
                return (
                  <tr
                    key={product.id}
                    className={cn(
                      "hover:bg-muted/30 transition-colors",
                      stock === 0 && "bg-red-50/30 dark:bg-red-950/10",
                      selectedIds.has(product.id) && "bg-primary/5"
                    )}
                    data-testid={`product-row-${product.id}`}
                  >
                    <td className="px-4 py-3">
                      <Checkbox checked={selectedIds.has(product.id)} onCheckedChange={() => toggleSelect(product.id)} aria-label={`Ürün seç ${product.name}`} />
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {product.image_url ? (
                          <img src={product.image_url} alt="" className="w-10 h-10 rounded-lg object-cover border shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0"><ImageIcon className="h-4 w-4 text-muted-foreground" /></div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{product.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-mono">SKU: {product.sku?.substring(0, 12)}{product.sku?.length > 12 ? '…' : ''}</Badge>
                            {product.barcode && <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-mono">{product.barcode?.substring(0, 13)}</Badge>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={cn("font-semibold tabular-nums text-sm", stock === 0 ? "text-red-600" : stock < 10 ? "text-amber-600" : "")}>{stock}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold tabular-nums text-sm">{formatCurrency(product.price || 0)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {detailTargets.map((integration) => {
                          const provider = String(integration.provider || "").toLowerCase();
                          const matched = !!getLinkValue(product, provider);
                          if (provider === "parasut" || provider === "bizimhesap" || provider === "benimpos") {
                            return <IntegrationLogo key={`${product.id}-${provider}`} provider={provider} matched={matched} title={`${integration.name || provider}${matched ? " · Eşleşti" : " · Eşleşmedi"}`} onClick={() => navigate(`/panel/urunler/${product.id}`)} />;
                          }
                          return <IntegrationLogo key={`${product.id}-${integration.id}`} provider={provider} matched={matched} title={`${integration.name || provider}${matched ? " · Eşleşti" : " · Eşleşmedi"}`} onClick={() => openMatchDialog(product, integration)} />;
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="outline" size="sm" className="h-8 min-w-[104px] text-xs" onClick={() => navigate(`/panel/urunler/${product.id}`)} data-testid={`view-product-${product.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1.5" />Görüntüle
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredProducts.length > 0 && (
            <div className="px-6 py-3 border-t bg-muted/30 flex items-center justify-between text-sm text-muted-foreground">
              <span>{filteredProducts.length} / {products.length} ürün · sayfa {safePage}/{totalPages}</span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}><ChevronLeft className="h-4 w-4" /></Button>
                <span className="px-2 tabular-nums">{safePage} / {totalPages}</span>
                <Button variant="ghost" size="sm" disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
          </div>{/* hidden md:block */}
      </div>{/* table-shell */}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ürün Ekle</DialogTitle>
            <DialogDescription>Eklemek istediğiniz yöntemi seçin.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Button
              variant="outline"
              className="justify-start"
              onClick={handleImportFromAccounting}
              disabled={importingAccounting || !accountingProvider}
            >
              {importingAccounting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              {accountingProvider
                ? `${accountingProvider === "parasut" ? "Paraşüt" : accountingProvider === "bizimhesap" ? "BizimHesap" : accountingProvider} Ürünlerini Çek`
                : "Muhasebe Bağlantısı Yok"}
            </Button>
            {accountingProvider === "bizimhesap" && (
              <Button
                variant="outline"
                className="justify-start"
                onClick={handleImportInStockFromBizimhesap}
                disabled={importingInStockOnly}
              >
                {importingInStockOnly ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                {importingInStockOnly ? "Stoktakiler çekiliyor..." : "BizimHesap'tan Stoktakileri Çek"}
              </Button>
            )}
            <Button className="justify-start" onClick={handleCreateBlankProduct}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Boş Ürün Ekle
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => {
                setAddOpen(false);
                toast.info("Excel ile yükleme ekranı hazırlanıyor.");
              }}
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Excel Dosyası ile Yükle
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => {
                setAddOpen(false);
                toast.info("XML ile yükleme ekranı hazırlanıyor.");
              }}
            >
              <FileCode2 className="mr-2 h-4 w-4" />
              XML Kaynağı ile Yükle
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={matchOpen} onOpenChange={setMatchOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Entegrasyon Ürünü Eşleştir</DialogTitle>
            <DialogDescription>
              {matchIntegration?.name || "-"} için ürün eşleştirme. Ürün adı veya stok kodu ile aratıp mevcut ürünü seçebilirsiniz.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border p-4 opacity-60">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">Yeni Ürün Oluştur</p>
                  <p className="text-xs text-muted-foreground">Bu özellik yakında aktif olacak.</p>
                </div>
                <Button variant="outline" size="sm" disabled>
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Deaktif
                </Button>
              </div>
            </div>

            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">Mağazadaki Mevcut Ürünü Seç</p>
                  <p className="text-xs text-muted-foreground">Seçtiğiniz ürünün entegrasyon kimliği mevcut ürüne bağlanır.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  value={matchSearch}
                  onChange={(e) => setMatchSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") fetchMatchCandidates(matchIntegration?.id, matchSearch, matchProduct?.id);
                  }}
                  placeholder="Ürün adı veya stok kodu yazın..."
                />
                <Button variant="outline" onClick={() => fetchMatchCandidates(matchIntegration?.id, matchSearch, matchProduct?.id)} disabled={matchLoading}>
                  {matchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>

              <div className="max-h-72 overflow-auto rounded-md border divide-y">
                {matchLoading ? (
                  <div className="p-4 text-sm text-muted-foreground">Yükleniyor...</div>
                ) : matchCandidates.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground">Eşleştirilecek mağaza ürünü bulunamadı.</div>
                ) : (
                  matchCandidates.map((item) => {
                    const isSelected = selectedCandidate?.id === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setSelectedCandidate(item)}
                        className={cn(
                          "w-full text-left p-3 transition",
                          isSelected ? "bg-emerald-50 border-l-2 border-emerald-400" : "hover:bg-muted/50"
                        )}
                      >
                        <p className="text-sm font-medium">{item.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Stok: {item.sku || "-"} · Barkod: {item.barcode || "-"}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Kanal ID: {item.external_id}
                        </p>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="flex items-center justify-between pt-1">
                <div className="text-xs text-muted-foreground">
                  Seçili ürün: {selectedCandidate?.name || "-"}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setMatchOpen(false)}>Vazgeç</Button>
                  <Button onClick={saveMatch} disabled={!selectedCandidate || savingMatch}>
                    {savingMatch ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                    Kaydet
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
